import { Text } from '@mantine/core';
import { useQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import type { CustomerFilters, CustomerListing } from '@apple/features/customer/listing.models';
import { customerOdataQueryOptions } from '@apple/features/customer/odata.queries';
import { programsQueryOptions } from '@apple/features/program/queries';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/customers')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.CustomerManagement',
		});
	},
	component: CustomerManageRoute,
});

function CustomerManageRoute() {
	const { t } = useTranslation('manage');
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();

	const tableState = useTableState<CustomerListing, CustomerFilters>({
		search,
		navigate,
		defaultSorting: [{ id: 'name', desc: true }],
		fieldMap: [],
	});

	const [customerSearchQuery, programsQuery] = useQueries({
		queries: [
			customerOdataQueryOptions({
				pagination: tableState.state.pagination,
				filters: tableState.currentFilterData,
				// sorting: tableState.value.sorting,
			}),
			programsQueryOptions(),
		],
	});

	const table = useTable({
		features,
		data: customerSearchQuery.data.rows,
		rowCount: customerSearchQuery.data.totalRowCount,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		meta: {
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'customerGroupId':
						return new Map(programsQuery.data?.map(x => [x.id, 1]));
					default:
						return undefined;
				}
			},
		},
		columnResizeMode: 'onChange',
		columns: [
			{
				accessorKey: 'name',
				header: t('customers.fields.customerName'),
				filter: {
					group: t('customers.filterGroups.details'),
				},
				cell: ({ row }) => (
					<Link
						to='/manage/customers/$customerCode'
						state={{ from: location }}
						params={{ customerCode: row.original.code }}
						title={row.original.name}
						children={row.original.name}
					/>
				),
			},
			{
				//id: 'customerCode',
				accessorKey: 'code',
				header: t('customers.fields.customerCode'),
				filter: {
					group: t('customers.filterGroups.details'),
				},
				cell: ({ row }) => <Text>{row.original.code}</Text>,
			},
			{
				accessorKey: 'customerGroupId',
				header: t('customers.fields.program'),
				size: 100,
				filter: {
					group: t('customers.filterGroups.details'),
					variant: 'select',
					getFilterDisplayValue: value =>
						programsQuery.data?.find(row => row.id === value)?.name ??
						(value as string),
				},
				cell: ({ row }) => <Text>{row.original.customerGroupName}</Text>,
			},
			{
				accessorKey: 'isActive',
				header: t('customers.fields.isActive'),
				size: 100,
				cell: ({ row }) => <Text>{row.original.isActive ? 'Active' : 'Inactive'}</Text>,
				filter: {
					group: t('customers.filterGroups.details'),
					variant: 'switch',
				},
			},
		],
	});

	return (
		<TableLayout
			title={t('customers.title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('customers.buttons.add.tooltip')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/manage/customers/add',
							state: { from: location },
							viewTransition: false,
						})
					}
					data-testid='add-customer'
				/>,
			]}
		>
			<DataTable table={table} loading={customerSearchQuery.isFetching} />
		</TableLayout>
	);
}
